import Card from "@/components/common/Card";
import FormField from "@/components/common/FormField";
import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { after, showToastNotification } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import { errorTypes } from "@/utility/validation";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import type { CreateRegularizationPayload } from "@/interface/regularization.interface";
import { RegularizationService } from "@/services/Regularization/RegularizationServices";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { ThrowError } from "@/api/callApi";
import FullPageLoader from "@/components/common/FullPageLoader";
import moment from "moment";

const CreateRegularization: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { userId, siteId } = useCurrentUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm<CreateRegularizationPayload>({
    defaultValues: {
      duration: "FULL_DAY",
      checkInDate: "",
      checkOutDate: "",
      reason: "",
    },
    mode: "onChange",
  });

  const minDate = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
  const today = moment().format("YYYY-MM-DD"); // YYYY-MM-DD
  const isEdit = Boolean(id);
  const selectedDuration = watch("duration") || "FULL_DAY";
  const checkInDate = watch("checkInDate");
  const firstHalfCheckInTime = watch("firstHalfCheckInTime");
  const firstHalfCheckOutTime = watch("firstHalfCheckOutTime");
  const secondHalfCheckInTime = watch("secondHalfCheckInTime");

  // Fetch data if in edit mode
  useEffect(() => {
    if (!isEdit) return;

    const fetchData = async () => {
      setIsFetching(true);
      try {
        const response = await RegularizationService.getById(id!);
        if (response.status && response.data) {
          const data = response.data;
          setValue("checkInDate", data.checkInDate);
          setValue("checkOutDate", data.checkOutDate);
          setValue("duration", data.duration || "FULL_DAY");
          setValue("firstHalfCheckInTime", data.firstHalfCheckInTime);
          setValue("firstHalfCheckOutTime", data.firstHalfCheckOutTime);
          setValue("secondHalfCheckInTime", data.secondHalfCheckInTime);
          setValue("secondHalfCheckOutTime", data.secondHalfCheckOutTime);
          setValue("reason", data.reason);
          // if (data.attendanceId) {
          //   setValue("attendanceId", data.attendanceId);
          // }
        } else {
          showToastNotification(
            "danger",
            "Failed to load regularization details",
          );
          navigate(-1);
        }
      } catch (error) {
        showToastNotification("danger", "Error fetching regularization");
        navigate(-1);
      } finally {
        setIsFetching(false);
      }
    };
    fetchData();
  }, [id, isEdit, setValue, navigate]);

  const onSubmit = async (data: CreateRegularizationPayload) => {
    if (!userId) {
      showToastNotification("warning", "User not logged in");
      return;
    }

    const formatTimeToSeconds = (timeStr?: string): string | undefined => {
      if (!timeStr) return undefined;
      if (timeStr.split(":").length === 2) {
        return `${timeStr}:00`;
      }
      return timeStr;
    };

    // Initialize base payload
    const payload: CreateRegularizationPayload = {
      userId: userId,
      siteId: siteId,
      // attendanceId: data.attendanceId || undefined,
      checkInDate: data.checkInDate,
      checkOutDate: data.checkInDate, // always same as checkInDate
      duration: data.duration,
      reason: data.reason,
    };

    // Set times based on duration option
    if (data.duration === "FULL_DAY") {
      payload.firstHalfCheckInTime = formatTimeToSeconds(
        data.firstHalfCheckInTime,
      );
      payload.firstHalfCheckOutTime = formatTimeToSeconds(
        data.firstHalfCheckOutTime,
      );
      payload.secondHalfCheckInTime = formatTimeToSeconds(
        data.secondHalfCheckInTime,
      );
      payload.secondHalfCheckOutTime = formatTimeToSeconds(
        data.secondHalfCheckOutTime,
      );
    } else if (data.duration === "FIRST_HALF") {
      payload.firstHalfCheckInTime = formatTimeToSeconds(
        data.firstHalfCheckInTime,
      );
      payload.firstHalfCheckOutTime = formatTimeToSeconds(
        data.firstHalfCheckOutTime,
      );
    } else if (data.duration === "SECOND_HALF") {
      payload.secondHalfCheckInTime = formatTimeToSeconds(
        data.secondHalfCheckInTime,
      );
      payload.secondHalfCheckOutTime = formatTimeToSeconds(
        data.secondHalfCheckOutTime,
      );
    }

    try {
      setIsLoading(true);
      let response;
      if (isEdit) {
        response = await RegularizationService.update(id!, payload);
      } else {
        response = await RegularizationService.create(payload);
      }

      if (response.status) {
        showToastNotification(
          "success",
          response.message ||
          (isEdit
            ? "Regularization updated successfully"
            : "Regularization applied successfully"),
        );
        navigate(RoutesLink.myRegularization || (-1 as any));
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDuration === "FULL_DAY") {
      if (
        firstHalfCheckInTime ||
        firstHalfCheckOutTime ||
        secondHalfCheckInTime
      ) {
        trigger([
          "firstHalfCheckOutTime",
          "secondHalfCheckInTime",
          "secondHalfCheckOutTime",
        ]);
      }
    } else if (selectedDuration === "FIRST_HALF") {
      if (firstHalfCheckInTime) {
        trigger("firstHalfCheckOutTime");
      }
    } else if (selectedDuration === "SECOND_HALF") {
      if (secondHalfCheckInTime) {
        trigger("secondHalfCheckOutTime");
      }
    }
  }, [
    firstHalfCheckInTime,
    firstHalfCheckOutTime,
    secondHalfCheckInTime,
    selectedDuration,
    trigger,
  ]);

  useEffect(() => {
    if (checkInDate) {
      setValue("checkOutDate", checkInDate, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [checkInDate, setValue]);

  if (isFetching) {
    return (
      <Layout>
        <FullPageLoader />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading
          title={isEdit ? "Edit Regularization" : "Apply for Regularization"}
          isBackButtonVisible
          onClick={() => navigate(-1)}
        />
        <Card className="overflow-visible">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Check-in Date */}
              {/* <FormField
                {...register("checkInDate", {
                  ...errorTypes.required("Check-in Date"),
                })}
                type="date"
                label="Check-in Date"
                max={today}
                onChange={(e) => {
                  const selected = e.target.value;
                  setValue("checkInDate", selected);
                  setValue("checkOutDate", selected);
                }}
                error={errors.checkInDate?.message as string}
              /> */}

              <FormField
                {...register("checkInDate", {
                  ...errorTypes.required("Check-in Date"),
                  validate: (value: string) => {
                    if (!value) return true;
                    if (value < minDate) {
                      return `Check-in Date cannot be earlier than last month (${moment(minDate).format("DD-MM-YYYY")})`;
                    }
                    if (value > today) {
                      return "Future dates are not allowed for Regularization";
                    }
                    return true;
                  },
                })}
                type="date"
                label="Check-in Date"
                min={minDate}
                max={today}
                onChange={(e: any) => {
                  const val = e.target.value;
                  setValue("checkInDate", val, { shouldValidate: true, shouldDirty: true });
                  setValue("checkOutDate", val, { shouldValidate: true, shouldDirty: true });
                }}
                error={errors.checkInDate?.message as string}
                required
              />

              <FormField
                {...register("checkOutDate", {
                  ...errorTypes.required("Check-out Date"),
                  validate: (value: string) => {
                    if (!value) return true;
                    if (value < minDate) {
                      return `Check-out Date cannot be earlier than last month (${moment(minDate).format("DD-MM-YYYY")})`;
                    }
                    if (value > today) {
                      return "Future dates are not allowed for Regularization";
                    }
                    return true;
                  },
                })}
                type="date"
                label="Check-out Date"
                min={minDate}
                max={today}
                value={watch("checkInDate") || ""}
                readOnly
                error={errors.checkOutDate?.message as string}
                required
              />

              <FormField
                {...register("duration", {
                  ...errorTypes.required("Duration"),
                })}
                type="select"
                label="Duration"
                value={selectedDuration}
                options={[
                  { value: "FULL_DAY", label: "Full Day" },
                  { value: "FIRST_HALF", label: "First Half" },
                  { value: "SECOND_HALF", label: "Second Half" },
                ]}
                error={errors.duration?.message as string}
                required
              />

              {selectedDuration === "FULL_DAY" && (
                <>
                  <FormField
                    {...register("firstHalfCheckInTime", {
                      ...errorTypes.required("First Half Check-in Time"),
                    })}
                    type="time"
                    label="First Half Check-in Time"
                    error={errors.firstHalfCheckInTime?.message as string}
                    required
                  />

                  <FormField
                    {...register("firstHalfCheckOutTime", {
                      ...errorTypes.required("First Half Check-out Time"),
                      validate: after(
                        firstHalfCheckInTime,
                        "First Half Check-out Time",
                      ),
                    })}
                    type="time"
                    label="First Half Check-out Time"
                    error={errors.firstHalfCheckOutTime?.message as string}
                    required
                  />

                  <FormField
                    {...register("secondHalfCheckInTime", {
                      ...errorTypes.required("Second Half Check-in Time"),
                      validate: after(
                        firstHalfCheckOutTime,
                        "Second Half Check-in Time",
                      ),
                    })}
                    type="time"
                    label="Second Half Check-in Time"
                    error={errors.secondHalfCheckInTime?.message as string}
                    required
                  />

                  <FormField
                    {...register("secondHalfCheckOutTime", {
                      ...errorTypes.required("Second Half Check-out Time"),
                      validate: after(
                        secondHalfCheckInTime,
                        "Second Half Check-out Time",
                      ),
                    })}
                    type="time"
                    label="Second Half Check-out Time"
                    error={errors.secondHalfCheckOutTime?.message as string}
                    required
                  />
                </>
              )}

              {selectedDuration === "FIRST_HALF" && (
                <>
                  <FormField
                    {...register("firstHalfCheckInTime", {
                      ...errorTypes.required("First Half Check-in Time"),
                    })}
                    type="time"
                    label="First Half Check-in Time"
                    error={errors.firstHalfCheckInTime?.message as string}
                    required
                  />

                  <FormField
                    {...register("firstHalfCheckOutTime", {
                      ...errorTypes.required("First Half Check-out Time"),
                      validate: after(
                        firstHalfCheckInTime,
                        "First Half Check-out Time",
                      ),
                    })}
                    type="time"
                    label="First Half Check-out Time"
                    error={errors.firstHalfCheckOutTime?.message as string}
                    required
                  />
                </>
              )}

              {selectedDuration === "SECOND_HALF" && (
                <>
                  <FormField
                    {...register("secondHalfCheckInTime", {
                      ...errorTypes.required("Second Half Check-in Time"),
                    })}
                    type="time"
                    label="Second Half Check-in Time"
                    error={errors.secondHalfCheckInTime?.message as string}
                    required
                  />

                  <FormField
                    {...register("secondHalfCheckOutTime", {
                      ...errorTypes.required("Second Half Check-out Time"),
                      validate: after(
                        secondHalfCheckInTime,
                        "Second Half Check-out Time",
                      ),
                    })}
                    type="time"
                    label="Second Half Check-out Time"
                    error={errors.secondHalfCheckOutTime?.message as string}
                    required
                  />
                </>
              )}

              <FormField
                {...register("reason", {
                  ...errorTypes.required("Reason"),
                  ...errorTypes.description
                })}
                type="textarea"
                label="Reason"
                rows={3}
                minLength={10}
                maxLength={500}
                placeholder="Enter reason for regularization"
                error={errors.reason?.message as string}
                required
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading
                  ? isEdit
                    ? "Updating..."
                    : "Submitting..."
                  : isEdit
                    ? "Update Regularization"
                    : "Submit Regularization"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default CreateRegularization;
